export interface InstallationStorageMismatchInput {
  isForbiddenRequest: boolean
  /** True while the storage, installation or farm data is still being fetched. */
  isDataLoading: boolean
  selectedStorageId?: string
  isServiceUser: boolean
  installation?: {
    emsMasterDeviceId?: string
    isFarm: boolean
    decommissionedAt?: string | null
  }
  /** The device link that belongs to the installation's EMS master. */
  storageDeviceLink?: { deviceId: string | null }
  farm: {
    isFarm: boolean
    isError: boolean
    masterStorageId?: string
    clientStorageIds: string[]
  }
}

export interface InstallationStorageMismatchStatus {
  isChecking: boolean
  isMismatch: boolean
}

/**
 * Decides whether the selected storage belongs to the installation of the current route.
 *
 * While the data needed for the decision is not available yet, `isChecking` is true and `isMismatch` is false,
 * so callers can wait instead of redirecting prematurely.
 */
export const resolveInstallationStorageMismatch = ({
  isForbiddenRequest,
  isDataLoading,
  selectedStorageId,
  isServiceUser,
  installation,
  storageDeviceLink,
  farm,
}: InstallationStorageMismatchInput): InstallationStorageMismatchStatus => {
  const installationStorageId = installation?.emsMasterDeviceId
  const hasValidStorageIds = Boolean(selectedStorageId) && Boolean(installationStorageId)

  if (isForbiddenRequest) return { isChecking: false, isMismatch: true }

  if (hasValidStorageIds && isDataLoading) return { isChecking: true, isMismatch: false }

  if (isServiceUser && installation?.decommissionedAt !== null) {
    return { isChecking: false, isMismatch: !storageDeviceLink || storageDeviceLink.deviceId !== selectedStorageId }
  }

  if (!hasValidStorageIds) return { isChecking: false, isMismatch: false }

  if (installation?.isFarm) {
    // After a reload the installation can be available before the farm context has derived the farm master and clients.
    // Validating against the still empty farm data would wrongly report a mismatch.
    if (!farm.isFarm && !farm.isError) return { isChecking: true, isMismatch: false }

    const validStorageIds = [farm.masterStorageId, ...farm.clientStorageIds]

    return {
      isChecking: false,
      isMismatch: !validStorageIds.includes(selectedStorageId) || installationStorageId !== farm.masterStorageId,
    }
  }

  return { isChecking: false, isMismatch: selectedStorageId !== installationStorageId }
}
